<template>
  <div class="moment-popup-wrapper" :style="style">
    <div ref="popup" class="moment-popup">
      <div class="level is-marginless" v-for="(item, index) in content" :key="index" :class="{ 'is-active': item.id === activeTrackerCategory }">
        <div class="level-left">
          <div class="category">{{ item.category }}</div>
        </div>
        <div class="level-right">
          <div class="value">{{ item.value }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { filter, get } from 'lodash';
import { arrToStr } from 'utils/string';
import { mapState, mapGetters } from 'vuex';
import { getEventKeypath } from 'config/settings';

export default {
  name: 'MomentPopup',

  props: {
    data: Object,
    position: Object,
  },

  data() {
    return {
      style: '',
    };
  },

  computed: {
    ...mapState('audience', ['activeTrackerCategory']),

    ...mapGetters('account', ['momentDefinitions']),

    content() {
      return this.data ? this.getContent(this.data) : '';
    },

    html() {
      return this.content.length ? this.$el.innerHTML : '';
    },
  },

  watch: {
    position(value) {
      if (!value) {
        return;
      }

      this.$nextTick(() => {
        if (!this.position || !this.$el) {
          return;
        }

        const { x, y } = this.position;
        const rect = this.$refs.popup.getBoundingClientRect();
        const offset = 16;
        const top = y - rect.height / 2;
        let left = x + offset;

        if (window.innerWidth < left + rect.width) {
          left = x - offset - rect.width;
        }

        this.style = { left: `${left}px`, top: `${top}px` };
      });
    },
  },

  methods: {
    moveTo(pos) {
      this.$nextTick(() => {
        console.log('update', pos);
        this.pos = pos;
      });
    },

    getContent(data) {
      let { past, present, future } = data;

      past = this.localizeContentItems(past);
      present = this.localizeContentItems(present);
      future = this.localizeContentItems(future);

      let content = [
        {
          id: 'past',
          category: this.$t('audience.moment_tracker.categories.past'),
          value: arrToStr(past),
        },
        {
          id: 'present',
          category: this.$t('audience.moment_tracker.categories.present'),
          value: arrToStr(present),
        },
        {
          id: 'future',
          category: this.$t('audience.moment_tracker.categories.future'),
          value: arrToStr(future),
        },
      ];

      return filter(content, item => item.value);
    },

    localizeContentItems(items) {
      return items.map(item => {
        // item is moment
        if (this.momentDefinitions.hasOwnProperty(item)) {
          const moment = this.momentDefinitions[item];

          return get(moment, 'display_name');
        }

        const keypath = getEventKeypath(item);

        // item is event
        if (keypath !== item) {
          return this.$t(keypath);
        }

        // item is unknown
        return item;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.moment-popup-wrapper {
  position: fixed;
  z-index: 999;
}

.moment-popup {
  position: relative;
  background-color: $white;
  border-radius: 2px;
  border: 1px solid rgba(151, 151, 151, 0.2);
  font-size: $size-small;
  line-height: 1.5;
  color: rgba($black, 0.87);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.level {
  position: relative;
  padding: 0.125rem 0.875rem;

  &.is-active {
    background-color: rgba($black, 0.06);
    color: rgba($black, 0.64);

    .category,
    .value {
      opacity: 1;
    }
  }
}

.level-left {
  + .level-right {
    margin-left: 1.25rem;
  }
}

.category,
.value {
  opacity: 0.64;
}

.value {
  font-weight: $weight-bold;
}
</style>
